import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { TabBar } from '../components/TabBar';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DialogTopBar } from '../components/DialogTopBar';
import { toast } from 'sonner';
import { Archive, RotateCcw, Trash2, GraduationCap, ChefHat, FileText, Loader2, AlertTriangle } from 'lucide-react';

const ArchivePage = () => {
    const { getAuthHeaders, API, isAdmin } = useAuth();
    const [archivedStudents, setArchivedStudents] = useState([]);
    const [archivedLessons, setArchivedLessons] = useState([]);
    const [archivedAssessments, setArchivedAssessments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deleteInfo, setDeleteInfo] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [activeTab, setActiveTab] = useState('students');

    useEffect(() => { fetchArchivedData(); }, []);

    const fetchArchivedData = async () => {
        setLoading(true);
        try {
            const [studentsRes, lessonsRes, assessmentsRes] = await Promise.all([
                axios.get(`${API}/students/archived`, { headers: getAuthHeaders() }),
                axios.get(`${API}/lessons/archived`, { headers: getAuthHeaders() }),
                axios.get(`${API}/assessments/archived`, { headers: getAuthHeaders() })
            ]);
            setArchivedStudents(studentsRes.data);
            setArchivedLessons(lessonsRes.data);
            setArchivedAssessments(assessmentsRes.data);
        } catch (error) {
            console.error('Failed to fetch archived data:', error);
            toast.error('Failed to load archived items');
        } finally {
            setLoading(false);
        }
    };

    const handleRestore = async (type, id) => {
        try {
            await axios.post(`${API}/${type}/${id}/restore`, {}, { headers: getAuthHeaders() });
            const label = type === 'students' ? 'Student' : type === 'lessons' ? 'Lesson' : 'Assessment';
            toast.success(`${label} restored successfully`);
            fetchArchivedData();
        } catch (error) {
            toast.error('Failed to restore item');
        }
    };

    const initiateDelete = async (type, item) => {
        setItemToDelete({ type, item });
        try {
            const response = await axios.delete(
                `${API}/${type}/${item.id}?confirm=false`,
                { headers: getAuthHeaders() }
            );
            if (response.data.requires_confirmation) {
                setDeleteInfo(response.data);
            } else {
                setDeleteInfo(null);
            }
        } catch (error) {
            setDeleteInfo(null);
        }
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        setDeleting(true);
        try {
            await axios.delete(
                `${API}/${itemToDelete.type}/${itemToDelete.item.id}?confirm=true`,
                { headers: getAuthHeaders() }
            );
            toast.success('Permanently deleted');
            setDeleteModalOpen(false);
            setItemToDelete(null);
            setDeleteInfo(null);
            fetchArchivedData();
        } catch (error) {
            toast.error('Failed to delete');
        } finally {
            setDeleting(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    if (!isAdmin()) {
        return (
            <Layout>
                <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                    <Archive className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                    <h3 className="text-base font-semibold text-gray-700 mb-1">Admin Access Required</h3>
                    <p className="text-gray-500">Only administrators can access the archive</p>
                </div>
            </Layout>
        );
    }

    const tabs = [
        { id: 'students', label: 'Students', icon: GraduationCap, count: archivedStudents.length },
        { id: 'lessons', label: 'Lessons', icon: ChefHat, count: archivedLessons.length },
        { id: 'assessments', label: 'Assessments', icon: FileText, count: archivedAssessments.length },
    ];

    const renderItem = (item, type, IconComp) => {
        const title = type === 'students' ? item.name : item.title;
        const subtitle = type === 'students'
            ? `${item.student_id} \u2022 Year ${item.year}`
            : `Year ${item.year} \u2022 ${item.date}`;
        return (
            <div key={item.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50" data-testid={`archived-${type.slice(0,-1)}-${item.id}`}>
                <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <IconComp className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                        <p className="font-medium text-gray-900">{title}</p>
                        <p className="text-sm text-gray-500">{subtitle}</p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-400">Archived {formatDate(item.archived_at)}</span>
                    <Button variant="outline" size="sm" onClick={() => handleRestore(type, item.id)} data-testid={`restore-${type.slice(0,-1)}-${item.id}`}>
                        <RotateCcw className="w-4 h-4 mr-1" /> Restore
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => initiateDelete(type, item)} className="text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-${type.slice(0,-1)}-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        );
    };

    const getActiveData = () => {
        if (activeTab === 'students') return { data: archivedStudents, icon: GraduationCap, type: 'students' };
        if (activeTab === 'lessons') return { data: archivedLessons, icon: ChefHat, type: 'lessons' };
        return { data: archivedAssessments, icon: FileText, type: 'assessments' };
    };

    const { data: activeData, icon: ActiveIcon, type: activeType } = getActiveData();

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="archive-page">
                <div>
                    <h1 className="font-heading text-3xl font-bold text-ica-navy">Archive</h1>
                    <p className="text-gray-500 mt-1">Manage archived students, lessons and assessments</p>
                </div>

                <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} testIdPrefix="archive-tab" />

                <div className="bg-white rounded-lg border border-gray-200">
                    {loading ? (
                        <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy mx-auto" /></div>
                    ) : activeData.length > 0 ? (
                        <div className="divide-y divide-gray-100">
                            {activeData.map(item => renderItem(item, activeType, ActiveIcon))}
                        </div>
                    ) : (
                        <div className="text-center py-10">
                            <ActiveIcon className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                            <h3 className="text-base font-semibold text-gray-700 mb-1">No archived {activeTab}</h3>
                            <p className="text-gray-500">{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} that are archived will appear here</p>
                        </div>
                    )}
                </div>

                <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                    <DialogContent data-testid="delete-confirm-modal">
                        <DialogTopBar title={<><AlertTriangle className="w-5 h-5" /> Confirm Permanent Deletion</>} titleClassName="flex items-center gap-2 text-red-600">
    <Button size="sm" variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
                            <Button size="sm" onClick={confirmDelete} disabled={deleting} variant="destructive" data-testid="confirm-delete-btn">
                                {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</> : <><Trash2 className="w-4 h-4 mr-2" /> Delete Permanently</>}
                            </Button>
</DialogTopBar>
                        <div className="py-4">
                            <p className="text-gray-700 mb-4">
                                Are you sure you want to permanently delete{' '}
                                <strong>{itemToDelete?.type === 'students' ? itemToDelete?.item?.name : itemToDelete?.item?.title}</strong>?
                            </p>
                            {deleteInfo && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                                    <p className="text-red-700 text-sm font-medium mb-2">Warning: This will also delete:</p>
                                    <ul className="text-red-600 text-sm space-y-1">
                                        <li>- {deleteInfo.attendance_count} attendance records</li>
                                        <li>- {deleteInfo.grades_count} grade records</li>
                                    </ul>
                                </div>
                            )}
                            <p className="text-gray-500 text-sm">This action cannot be undone.</p>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

export default ArchivePage;
